/**
 * Browser speech-to-text for the composer.
 *
 * ## What this is, and what it is not
 *
 * This is the browser's own `SpeechRecognition`, transcribing into the text box so
 * the counselor can edit before sending. It is NOT the voice agent: that is the
 * Twilio telephony path (`routes/voice.ts`), which calls prospects and is gated on
 * `voice_agent`. The two share a word and nothing else.
 *
 * ⚠️ THE AUDIO LEAVES THE MACHINE. Chrome's implementation streams microphone audio
 * to Google's servers for recognition — it is not on-device. In this product people
 * dictate prospect names and, in a senior-living CRM, occasionally health details.
 * That is a real disclosure, which is why the button carries it in its title rather
 * than presenting dictation as a neutral convenience.
 *
 * Unsupported browsers get `supported: false` and the composer renders no button at
 * all, rather than one that fails on click. Safari and Firefox are the common cases.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** The vendor-prefixed constructor, as it actually exists on the two engines that
 *  ship it. Typed locally because `lib.dom` still does not declare it. */
type RecognitionCtor = new () => {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};

function getCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function useDictation(onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const [supported] = useState(() => getCtor() !== null);
  const recRef = useRef<InstanceType<RecognitionCtor> | null>(null);
  // The callback changes every render; keeping it in a ref means the recognition
  // handlers never close over a stale one without re-creating the recogniser.
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const stop = useCallback(() => {
    recRef.current?.stop();
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    recRef.current = rec;
    // The page's language, not a hardcoded locale — a hardcoded "en-US" mis-hears
    // every name in a tenant that does not speak it.
    rec.lang = document.documentElement.lang || navigator.language || "en-US";
    // Single utterance, final text only. Interim results stream half-words into the
    // box and read as the field glitching while you talk.
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const text = Array.from(
        { length: e.results.length },
        (_, i) => e.results[i]?.[0]?.transcript ?? "",
      )
        .join(" ")
        .trim();
      if (text) onTextRef.current(text);
    };
    // A denied mic permission and a network failure surface identically here, and
    // neither deserves a dialog: the text box still works.
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    try {
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, []);

  // Abort on unmount, or the recogniser keeps the mic open after the panel closes.
  useEffect(() => () => recRef.current?.abort(), []);

  return { supported, listening, start, stop };
}
